import axios, { AxiosError, AxiosResponse } from 'axios';
import { ChatMessage } from '../Components/Shared/API/ApiTypes';
import type {
    Answer, CatalogRequest, CatalogResponse,
    CollectedValuesRequest, CollectedValuesResponse,
    FormDismissRequest, FormDismissResponse,
    LogEvent, LogEventResponse, OpenFieldSpec,
    ProactiveRequest, ProactiveResponse, DismissRequest, DismissResponse,
} from './types';
export * from './types';

interface Question {
    question: string;
    index: string | null;
    sessionid: string | null;
    context: ChatMessage[];
    current_url?: string;
    /**
     * Conversational forms: only the fields still open for this turn (platform —
     * "Extraction is a per-turn contract over open fields only"). Omitted
     * entirely when the feature is off, so nothing changes for other tenants.
     */
    open_fields?: OpenFieldSpec[];
    form_objectid?: string;
    /**
     * Engagement (store tracker 6.55): sent only on the hidden activation
     * turn (Start/Switch/resume) — the form being activated, by objectid.
     * The turn carries no visitor-visible user message (`question` is sent
     * empty). Absent on every ordinary turn.
     */
    activate_form?: string;
    /**
     * Store tracker 6.64: fields the visitor has explicitly confirmed in the
     * form view (a manual edit) or via a suggestion confirm this session —
     * distinct from "has a value", which also covers a plain chat fill. Sent
     * on every turn while the conversational-forms feature is active (the
     * three call sites in `FormsContext.tsx`), so a misbehaving or stale
     * extraction pass never re-fills a field the visitor has locked in.
     * Omitted entirely on the legacy/no-forms path (`Utilities/SendMessage.ts`),
     * exactly like `open_fields`.
     */
    confirmed_field_ids?: string[];
    /**
     * Inline sensitive asks (store change `conversational-forms-inline-sensitive-asks`):
     * ids only — the active form's sensitive, form-only fields this build can
     * render an input control for inside the chat. Omitted when empty. Never
     * a value.
     */
    inline_field_ids?: string[];
    /**
     * The hidden "saved" marker turn: the id of the field the visitor just set
     * in the inline control. Sent with an empty `question`; the value itself
     * never travels.
     */
    inline_saved?: string;
    /**
     * The inline control's Skip: the id of the field the visitor skipped. Sent
     * on the visible "Skip for now" turn so the server hands the field to the
     * form by id. Absent on every other turn (a Retry resends text only).
     */
    inline_skipped?: string;
}
interface SubmitInformation {
    indexname: string | null;
    sessionid: string | null;
    firstname: string | null;
    lastname: string | null;
    email: string | null;
    visitortype: string | null;
}

interface SubmitDynamicInformationField {
    fieldid: string,
    fieldvalue: string,
    /**
     * Conversational forms: this value started as an AI suggestion the student
     * confirmed. Analytics-only — it anchors the backend's `FieldFilledViaChat`
     * to this write and never reaches the stored envelope, whose authority
     * stays form-explicit so a confirmed value keeps winning the merge.
     * Additive and defaulted false; a submit that omits it behaves as before.
     */
    chatorigin?: boolean,
    /** Mirrors the turn path's open-field `sensitive`: the event names the
     *  field but never carries its value. */
    sensitive?: boolean
}

interface SubmitDynamicInformation {
    indexname: string | null;
    sessionid: string | null;
    objectid: string | null;
    fields: SubmitDynamicInformationField[]
}

interface Query {
    query: string;
    index: string | null;
    sessionid: string | null;
    current_url?: string;
}

/**
 * TODO: Revisit after https://github.com/EdullaAI/Edulla.Bot.Interface/pull/52 is merged.
 * Leaving the current approach for now to avoid unnecessary conflicts.
 */
const newAPIUrl = 'https://botapi2.app.edulla.ai';
const newAPIKey = '6$E(^Ig}D&6.RLeX'
const localAPIUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const localAPIKey = 'your-api-key-here';
const isLocalhost = window.location.href.includes("localhost");
// const baseUrl = 'https://botapi.app.edulla.ai'
const baseUrl = isLocalhost ? localAPIUrl : newAPIUrl;
const apiKey = isLocalhost ? localAPIKey : newAPIKey;
const proactiveBaseUrl = baseUrl;
const proactiveApiKey = apiKey;

axios.defaults.baseURL = baseUrl
// axios.defaults.baseURL = 'http://localhost:8000'
axios.interceptors.response.use(
    (res) => res,
    (error: AxiosError) => {
        const { data, status } = error.response!;
        switch (status) {
            case 400:
                console.error(data);
                break;
            case 401:
                console.error('unauthorised');
                break;
            case 404:
                console.error('/not-found');
                break;
            case 500:
                console.error('/server-error');
                break;
        }
        return Promise.reject(error);
    }
);

const responseBody = <T>(response: AxiosResponse<T>) => response.data;

const request = {
    get: <T>(url: string) => axios.get<T>(url).then(responseBody),
    // The plain `get` above sends no credentials; routes behind the API key need
    // this one.
    getAuthed: <T>(url: string) =>
        axios<T>({
            url,
            baseURL: baseUrl,
            method: 'get',
            headers: { 'x-api-key': apiKey },
        }).then(responseBody),
    post: <T>(url: string, body: {}, baseUrlOverride = baseUrl, apiKeyOverride = apiKey) =>
        axios<T>({
            url,
            baseURL: baseUrlOverride,
            method: 'post',
            headers: { 'Content-Type': 'application/json', 'x-api-key': apiKeyOverride },
            data: body
        }).then(responseBody)
};

const question = {
    ask: (data: Question, customPrompt:boolean = false) => {
        if(data.index ==="mobap" || data.index === "neumann" || customPrompt)
            return request.post<Answer>('/question_custom_prompt/', JSON.stringify(data))
        return request.post<Answer>('/question/', JSON.stringify(data))
    }
};

const search = {
    start: (data: Query) => {
        return request.post<Answer>('/search/', JSON.stringify(data))
    }
};

const searchCatalog = {
    start: (data: CatalogRequest) => {
        return request.post<CatalogResponse>('/catalogsearch/', JSON.stringify(data))
    }
};

const catalog = {
    ask: (data: CatalogRequest) => {
        return request.post<CatalogResponse>('/catalog/', JSON.stringify(data), newAPIUrl, newAPIKey)
    }
};
const submitInformation = {
    submitInformation: (data: SubmitInformation) => {
        return request.post<Answer>('/submitinformation/', JSON.stringify(data))
    }
};

const submitDyanmicInformation = {
    submitDyanmicInformation: (data: SubmitDynamicInformation) => {
        return request.post<Answer>('/submitdyanmicinformation/', JSON.stringify(data))
    }
};
const questionStream = {
    ask: (data: Question, callback: (msg: any, msgType: string) => void) => {
        let lastMessagesString = JSON.stringify(data.context);
        let lastMessagesEncoded = btoa(lastMessagesString);
        const currentUrlParam = data.current_url ? `&current_url=${encodeURIComponent(data.current_url)}` : '';
        const evtSource = new EventSource(`${baseUrl}/questionstream?question=${data.question}&index=${data.index}&sessionid=${data.sessionid}&api-key=${encodeURIComponent(apiKey)}&context=${lastMessagesEncoded}${currentUrlParam}`)
        evtSource.addEventListener('new_message', function (event) {
            callback(event.data, "NewMessage")
        })
        evtSource.addEventListener('links', function (event) {
            callback(event.data, "Links")
        })
        evtSource.addEventListener('end_event', function (event) {
            callback(event.data, "Closed")
            evtSource.close()
        })
    }
}


const logEvent = {
    logEvent: (data: LogEvent) => {
        return request.post<LogEventResponse>('/logevent/', JSON.stringify(data))
    }
};

// Conversational forms. Both calls are made only while the feature is active
// (config carries conversational-form metadata), and both are failure-tolerant
// at the call site: a missing read starts the form from empty, a failed dismiss
// still completes the local discard.
const forms = {
    // A GET with query parameters, matching the deployed route signature.
    collected: (data: CollectedValuesRequest) => {
        const query = new URLSearchParams({
            sessionid: data.sessionid,
            index: data.index,
            objectid: data.objectid,
        });
        return request.getAuthed<CollectedValuesResponse>(`/forms/collected/?${query.toString()}`);
    },
    dismiss: (data: FormDismissRequest) =>
        request.post<FormDismissResponse>('/forms/dismiss/', JSON.stringify(data)),
};

const proactive = {
    fire: (data: ProactiveRequest) =>
        request.post<ProactiveResponse>('/proactive/', JSON.stringify(data), proactiveBaseUrl, proactiveApiKey),
    dismiss: (data: DismissRequest) =>
        request.post<DismissResponse>('/proactive/dismiss/', JSON.stringify(data), proactiveBaseUrl, proactiveApiKey),
};

const api = {
    question,
    questionStream,
    searchCatalog,
    search,
    catalog,
    submitInformation,
    submitDyanmicInformation,
    logEvent,
    proactive,
    forms,
};

export default api;
